import { Button } from "@kaltura/ds-react-components";
import { postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { useState } from "react";
import { createPortal } from "react-dom";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { Recyclebin24Icon } from "@kaltura/ds-react-icons";
import { ConfirmationModal } from "@kaltura/mediaspace-shared-ui";
import { ButtonAnalyticsProvider, useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

export interface EmptyRecycleBinButtonProps {
    onReload: () => void;
    url: string;
}

interface ActionResponse {
    success: boolean;
    error?: string;
}

/**
 * EmptyRecycleBinButton component - a button that allows users to empty the recycle bin.
 * @data-endpoint recyclebin-recycle-bin-ds-empty-recycle-bin
 */
export const EmptyRecycleBinButton = ({ onReload, url }: EmptyRecycleBinButtonProps) => {
    const [openConfirmation, setOpenConfirmation] = useState(false);
    const [processing, setProcessing] = useState(false);

    const { showToast } = useToastsContext();
    const sendButtonAnalytics = useButtonAnalytics();

    const closeConfirmation = () => {
        sendButtonAnalytics(
            "Cancel empty recycle bin",
            ButtonClickAnalyticsType.CLOSE,
            undefined,
            "recycle bin",
            "Modal - Recycle bin - Permanently delete"
        );
        setOpenConfirmation(false);
    };

    const handleClick = () => {
        setOpenConfirmation(true);
        sendButtonAnalytics("Empty recycle bin", ButtonClickAnalyticsType.DELETE);
    };

    const handleEmpty = async () => {
        setProcessing(true);
        sendButtonAnalytics(
            "Confirm empty recycle bin",
            ButtonClickAnalyticsType.DELETE,
            undefined,
            "recycle bin",
            "Modal - Recycle bin - Permanently delete"
        );

        try {
            const result: ActionResponse = await postKmsData(url);
            if (result.success) {
                showToast({ severity: "success", message: translate("Recycle bin has been emptied.") });
            }
            else {
                showToast({ severity: "error", message: translate("Failed to empty the recycle bin.") });
            }
        }
        catch (error) {
            console.error("Empty recycle bin error:", error);
            showToast({ severity: "error", message: translate("Failed to empty the recycle bin.") });
        }
        setProcessing(false);
        setOpenConfirmation(false);
        onReload();
    };

    /**
     * Render modal via Portal to escape the Suspense boundary, allowing it to
     * close immediately when onReload() triggers Suspense on the parent tree.
     */
    const modalContent = openConfirmation
        ? createPortal(
              <ButtonAnalyticsProvider location={"Empty Recycle Bin Modal"}>
                  <ConfirmationModal
                      yesCallback={handleEmpty}
                      yesButtonColor={"danger"}
                      yesButtonLabel={translate("Empty")}
                      open={openConfirmation}
                      noCallback={closeConfirmation}
                      title={translate("Empty recycle bin?")}
                      text={translate(
                          "This will permanently delete all items in the recycle bin. This action cannot be undone."
                      )}
                      processing={processing}
                  />
              </ButtonAnalyticsProvider>,
              document.body
          )
        : null;

    return (
        <>
            <Button variant={"borderless"} color={"secondary"} onClick={handleClick} startIcon={<Recyclebin24Icon />}>
                {translate("Empty recycle bin")}
            </Button>
            {modalContent}
        </>
    );
};
